import { useState, useEffect, useCallback } from 'react';
import { gpsApi } from '../../api/gps.api';
import { useUI } from '../../store/UIContext';
import { PageHeader, Btn, Spinner, Input } from '../../components/ui/index';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';

const EMPTY_FORM = { id: null, place_name: '', latitude: '', longitude: '', radius_meters: 1000 };

export default function GpsKnownPlacesPage() {
  const { addToast } = useUI();
  const [loading, setLoading] = useState(true);
  const [places, setPlaces] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    try {
      const res = await gpsApi.getKnownPlaces();
      setPlaces(res.data?.places || []);
    } catch {
      if (showSpinner) addToast('خطا در دریافت مکان‌ها', 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => { load(); }, [load]);
  useAutoRefresh(() => load(false), { paused: !!form.id });

  const resetForm = () => setForm(EMPTY_FORM);

  const handleEdit = (p) => {
    setForm({ id: p.id, place_name: p.place_name, latitude: p.latitude, longitude: p.longitude, radius_meters: p.radius_meters });
  };

  const handleUseMyLocation = () => {
    if (!('geolocation' in navigator)) {
      addToast('مرورگر شما از موقعیت‌مکانی پشتیبانی نمی‌کند', 'error');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setForm((f) => ({ ...f, latitude: position.coords.latitude, longitude: position.coords.longitude }));
      },
      () => addToast('دسترسی به موقعیت مکانی رد شد', 'error'),
    );
  };

  const handleSave = async () => {
    if (!form.place_name.trim() || form.latitude === '' || form.longitude === '') {
      addToast('نام مکان و مختصات الزامی است', 'error');
      return;
    }
    setSaving(true);
    try {
      const params = {
        place_name: form.place_name.trim(),
        latitude: parseFloat(form.latitude),
        longitude: parseFloat(form.longitude),
        radius_meters: parseInt(form.radius_meters, 10) || 1000,
      };
      if (form.id) {
        await gpsApi.updateKnownPlace({ id: form.id, ...params, is_active: true });
      } else {
        await gpsApi.addKnownPlace(params);
      }
      addToast('ذخیره شد', 'success');
      resetForm();
      load();
    } catch {
      addToast('خطا در ذخیره', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await gpsApi.deleteKnownPlace(id);
      addToast('حذف شد', 'success');
      load();
    } catch {
      addToast('خطا در حذف', 'error');
    }
  };

  return (
    <div className="fade-in">
      <PageHeader
        title="مکان‌های مجاز GPS"
        subtitle="دفتر مرکزی، سایت مشتری و... را تعریف کنید تا در گزارش روزانه مشخص شود کارمند در محل حضور داشته یا نه"
      />

      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, maxWidth: 640, marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Input
            label="نام مکان"
            value={form.place_name}
            onChange={(e) => setForm((f) => ({ ...f, place_name: e.target.value }))}
            placeholder="مثلاً دفتر مرکزی"
            wrapStyle={{ flex: '1 1 200px' }}
          />
          <Input
            label="عرض جغرافیایی (latitude)"
            value={form.latitude}
            onChange={(e) => setForm((f) => ({ ...f, latitude: e.target.value }))}
            placeholder="32.67633"
            wrapStyle={{ flex: '1 1 160px' }}
          />
          <Input
            label="طول جغرافیایی (longitude)"
            value={form.longitude}
            onChange={(e) => setForm((f) => ({ ...f, longitude: e.target.value }))}
            placeholder="51.64636"
            wrapStyle={{ flex: '1 1 160px' }}
          />
          <Input
            label="شعاع مجاز (متر)"
            value={form.radius_meters}
            onChange={(e) => setForm((f) => ({ ...f, radius_meters: e.target.value }))}
            placeholder="1000"
            wrapStyle={{ flex: '1 1 140px' }}
          />
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <Btn variant="primary" onClick={handleSave} loading={saving}>
            {form.id ? 'به‌روزرسانی' : 'افزودن'}
          </Btn>
          <Btn variant="ghost" onClick={handleUseMyLocation}>📍 موقعیت فعلی من</Btn>
          {form.id && <Btn variant="ghost" onClick={resetForm}>انصراف</Btn>}
        </div>
      </div>

      {loading ? <Spinner /> : (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', maxWidth: 640 }}>
          {places.length === 0 && (
            <div style={{ padding: 20, fontSize: 13, color: '#94a3b8' }}>هنوز مکانی تعریف نشده است.</div>
          )}
          {places.map((p) => (
            <div key={p.id} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px',
              borderBottom: '1px solid #f1f5f9',
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{p.place_name}</div>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>
                  {p.latitude}, {p.longitude} · شعاع {p.radius_meters} متر
                </div>
              </div>
              <Btn variant="ghost" size="sm" onClick={() => handleEdit(p)}>ویرایش</Btn>
              <Btn variant="danger" size="sm" onClick={() => handleDelete(p.id)}>حذف</Btn>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
